import { TypedDocumentNode, gql } from '@apollo/client';
import { faShoppingCart } from '@fortawesome/pro-solid-svg-icons';
import { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import {
  LoadingButton,
  Props as LoadingButtonProps,
} from '@sorare/core/src/atoms/buttons/LoadingButton';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { ConditionalWrapper } from '@sorare/core/src/atoms/layout/ConditionalWrapper';
import { Tooltip } from '@sorare/core/src/atoms/tooltip/Tooltip';
import { NotMobileAppFeature } from '@sorare/core/src/components/BuyableFeature';

import { useMarketplaceContext } from 'contexts/Marketplace';
import { useCart } from 'hooks/useCart';

import { AddToCart_anyCard } from './__generated__/index.graphql';

type Props = Omit<LoadingButtonProps, 'onClick' | 'loading'> & {
  anyCard: AddToCart_anyCard;
};

const StyledLoadingButton = styled(LoadingButton)`
  width: var(--quadruple-unit);
`;

const ConditionalTooltip = ({ children }: { children: ReactNode }) => (
  <Tooltip
    placement="top"
    title={
      <FormattedMessage
        id="addToCart.tooltip"
        defaultMessage="This item is already in your cart"
      />
    }
  >
    {children}
  </Tooltip>
);

export const AddToCart = ({ anyCard, children, ...rest }: Props) => {
  const { addCardToCart, isInCart } = useCart();
  const { showAddToCartPopin } = useMarketplaceContext();
  const inCart = !!isInCart(anyCard);

  return (
    <NotMobileAppFeature>
      <ConditionalWrapper wrap={inCart} Wrapper={ConditionalTooltip}>
        <StyledLoadingButton
          loading={addCardToCart.loading}
          disabled={inCart}
          onClick={() => {
            addCardToCart.mutate(anyCard).then(cart => {
              showAddToCartPopin(
                cart?.mainCartItems?.find(ci => ci.card.slug === anyCard.slug)
                  ?.card.slug || null
              );
            });
          }}
          {...rest}
        >
          {children || <FontAwesomeIcon icon={faShoppingCart} size="sm" />}
        </StyledLoadingButton>
      </ConditionalWrapper>
    </NotMobileAppFeature>
  );
};

AddToCart.fragments = {
  anyCard: gql`
    fragment AddToCart_anyCard on AnyCardInterface {
      slug
      ...useCart_anyCard
    }
    ${useCart.fragments.anyCard}
  ` as TypedDocumentNode<AddToCart_anyCard>,
};
